import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import githubService from '../utils/githubService';

const GitHubAuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // URL에서 code 파라미터 추출
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (!code) {
          throw new Error('인증 코드를 받지 못했습니다.');
        }

        console.log('🔐 GitHub 인증 중...');
        setStatus('authenticating');

        // 백엔드로 code를 보내서 access_token 받기
        console.log('📡 /api/github-auth 호출 시작...');
        const response = await fetch('/api/github-auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        console.log('📡 응답 상태:', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API 응답 에러:', errorText);
          throw new Error(`GitHub 인증에 실패했습니다: ${response.status}`);
        }

        const tokenData = await response.json();
        console.log('🎫 토큰 받기 성공');
        const { access_token } = tokenData;
        
        // GitHub Service 초기화 (retry 로직 포함)
        console.log('👤 GitHub 사용자 정보 가져오기 중...');
        
        let userInfo;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            userInfo = await githubService.initializeWithToken(access_token);
            console.log('👤 사용자 정보 가져오기 성공:', userInfo.login);
            break; // 성공하면 루프 종료
          } catch (error) {
            retryCount++;
            console.log(`🔄 GitHub API 호출 재시도 ${retryCount}/${maxRetries}:`, error.message);
            
            if (retryCount >= maxRetries) {
              throw error; // 최대 재시도 횟수 도달하면 에러 발생
            }
            
            // 재시도 전 잠시 대기 (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }
        
        // 토큰을 localStorage에 저장
        localStorage.setItem('github_access_token', access_token);
        
        console.log('✅ GitHub 로그인 성공:', userInfo.login);
        setStatus('success');
        
        // 2초 후 이전 페이지 또는 메인 페이지로 리디렉션
        setTimeout(() => {
          // localStorage에서 로그인 전 URL을 가져오거나 메인 페이지로 이동
          const returnUrl = localStorage.getItem('github_login_return_url') || '/';
          localStorage.removeItem('github_login_return_url'); // 사용 후 제거
          navigate(returnUrl);
        }, 2000);
        
      } catch (err) {
        console.error('❌ GitHub 인증 실패:', err);
        setError(err.message);
        setStatus('error');
        
        // 5초 후 메인 페이지로 리디렉션
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    };

    handleCallback();
  }, [navigate]);

  const renderStatus = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">처리 중...</h2>
            <p className="text-gray-600">GitHub 인증 정보를 확인하고 있습니다.</p>
          </div>
        );
      
      case 'authenticating':
        return (
          <div className="text-center">
            <div className="animate-pulse">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">GitHub 인증 중...</h2>
            <p className="text-gray-600">잠시만 기다려주세요.</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center">
            <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-green-800 mb-2">로그인 성공!</h2>
            <p className="text-gray-600">GitHub 계정으로 로그인되었습니다. 곧 메인 페이지로 이동합니다.</p>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center">
            <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">로그인 실패</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">5초 후 자동으로 메인 페이지로 이동합니다.</p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">GitHub 로그인</h1>
          <div className="w-12 h-1 bg-blue-500 mx-auto rounded"></div>
        </div>
        
        {renderStatus()}
      </div>
    </div>
  );
};

export default GitHubAuthCallback;